import React, { useMemo } from 'react';
import { Card, Row, Col, Typography, List, Space, InputNumber, Collapse, Tag, theme } from 'antd';
import { RoomData, Rate } from '@/AppRedux/actions/booking/bookingApiType';
import RoomPhotoGrid from "@/modules/booking/components/RoomPhotoGrid";
import { useIntl } from 'react-intl';
import { HtmlText } from "@/components/HtmlText";

interface RoomCardProps {
  roomData: RoomData;
  qtyMap: Record<string, number>;
  getCartKey: (room: RoomData, rate: Rate) => string;
  onQtyChange: (room: RoomData, rate: Rate, qty: number) => void;
  isMobile: boolean;
}

const RoomCardComponent: React.FC<RoomCardProps> = ({ roomData, qtyMap, getCartKey, onQtyChange, isMobile }) => {
  const { token } = theme.useToken();
  const intl = useIntl();

  // ผลรวม qty ของ rate ทั้งหมดใน room type นี้ (แชร์ quota เดียวกันจาก room_avail)
  const roomTypeQty = useMemo(
    () => roomData.rates.reduce((sum, rate) => sum + (qtyMap[getCartKey(roomData, rate)] || 0), 0),
    [roomData, qtyMap, getCartKey],
  );
  const roomTypeRemaining = roomData.room_avail - roomTypeQty;
  const showLowStock = roomTypeRemaining > 0 && roomTypeRemaining <= 3;
  return (
    <Card
      styles={{ body: { padding: isMobile ? '12px' : '24px' } }}
      style={{ width: '100%', marginBottom: 24 }}
    >
      <Row gutter={[16, 16]}>
        {/* ส่วนรูปภาพ (ซ้าย) */}
        <Col xs={24} md={8}>
          <Typography.Title level={2} style={{ padding: 4, fontSize: isMobile ? '20px' : '24px' }}>
            {roomData.roomtype_name}
          </Typography.Title>
          <div style={{ position: 'relative', overflow: 'hidden', borderRadius: '8px', minHeight: 160 }}>
            {showLowStock && (
              <div
                style={{
                  position: 'absolute',
                  top: 8,
                  left: 8,
                  zIndex: 1,
                  pointerEvents: 'none',
                  padding: '2px 10px',
                  borderRadius: 4,
                  fontSize: 12,
                  fontWeight: 600,
                  color: '#fff',
                  backgroundColor: roomTypeRemaining <= 1 ? token.colorError : token.colorWarning,
                }}
              >
                🔥 {intl.formatMessage({ id: 'room.low_stock', defaultMessage: 'Only {count} left!' }, { count: roomTypeRemaining })}
              </div>
            )}
            <RoomPhotoGrid
              images={roomData.room_images}
              thumbnail={roomData.room_cover_image}
              name={roomData.roomtype_name}
              variant={isMobile ? "mobile" : "desktop"}
            />
          </div>
          {roomData.room_description && (
            <Typography.Paragraph
              type="secondary"
              style={{ marginTop: 8, marginBottom: 8 }}
            >
              <HtmlText value={roomData.room_description} />
            </Typography.Paragraph>
          )}

          {/* Amenities */}
          {roomData.amenities?.length ? (
            <Collapse
              ghost
              size="small"
              defaultActiveKey={isMobile ? [] : ['1']}
              style={{ marginBottom: 8 }}
              items={[
                {
                  key: "1",
                  label: intl.formatMessage({ id: 'room.amenities', defaultMessage: 'Amenities' }),
                  children: (
                    <ul style={{ paddingLeft: 22, margin: 0 }}>
                      {roomData.amenities.map(a => (
                        <li key={a}>{a}</li>
                      ))}
                    </ul>
                  )
                }
              ]}
            />
          ) : null}
        </Col>

        {/* ส่วนรายการราคา (ขวา) */}
        <Col xs={24} md={16}>
          <List
            dataSource={roomData.rates}
            split={true}
            renderItem={(rate: Rate) => {
              const key = getCartKey(roomData, rate);
              const currentQty = qtyMap[key] || 0;
              const otherRatesQty = roomTypeQty - currentQty;
              const maxForRate = Math.max(0, roomData.room_avail - otherRatesQty);
              return (
                <List.Item key={key} style={{ padding: '16px 0', display: 'block' }}>

                  {/* 1. SECTION HEADER: ชื่อ Rate Plan + ปุ่ม Control */}
                  <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', marginBottom: 12 }}>

                    {/* ชื่อ Rate Plan */}
                    <div style={{ flex: 1, paddingRight: 8 }}>
                      <Typography.Title level={5} style={{ margin: 0, fontSize: isMobile ? '16px' : '18px', lineHeight: 1.4 }}>
                        {rate.rateplan_name}
                      </Typography.Title>

                      {/* ราคา */}
                      <Typography.Text strong style={{ fontSize: isMobile ? '16px' : '18px', display: 'block', marginTop: 6 }}>
                        ฿ {rate.room_rate.toLocaleString()} <span style={{ fontSize: '13px', color: '#8c8c8c', fontWeight: 'normal' }}>
                          {intl.formatMessage({ id: 'room.price_per_night', defaultMessage: '/ night' })}
                        </span>
                      </Typography.Text>
                    </div>

                    {/* ปุ่ม Control */}
                    <div style={{ flex: 'none' }}>
                      <Space.Compact style={{ verticalAlign: 'middle' }}>
                        <InputNumber
                          mode='spinner'
                          min={0}
                          max={maxForRate}
                          value={currentQty}
                          style={{
                            width: isMobile ? 120 : 120,
                            textAlign: 'center',
                            fontWeight: 'bold'
                          }}
                          onChange={(v) => onQtyChange(roomData, rate, Number(v || 0))}
                          size={isMobile ? 'middle' : 'large'}
                        />
                      </Space.Compact>
                    </div>
                  </div>

                  {/* 2. SECTION DESCRIPTION: รายละเอียดแบบมีไอคอน */}
                  {rate.rate_description && (
                    <div style={{
                      marginTop: 8,
                      marginBottom: 12,
                      fontSize: isMobile ? '13px' : '14px',
                      lineHeight: 1.6,
                      color: token.colorTextSecondary
                    }}>
                      <HtmlText value={rate.rate_description} />
                    </div>
                  )}

                  {/* 3. SECTION TAGS: Tags และเงื่อนไขต่างๆ */}
                  <div style={{ marginTop: 8 }}>
                    <Space size={[4, 8]} wrap style={{ width: '100%' }}>
                      {rate.abf_included && <Tag color="green" bordered={false}>{intl.formatMessage({ id: 'rate.includes_breakfast', defaultMessage: 'Breakfast included' })}</Tag>}
                      {rate.lunch_included && <Tag color="cyan" bordered={false}>{intl.formatMessage({ id: 'rate.includes_lunch', defaultMessage: 'Lunch included' })}</Tag>}
                      {rate.dinner_included && <Tag color="geekblue" bordered={false}>{intl.formatMessage({ id: 'rate.includes_dinner', defaultMessage: 'Dinner included' })}</Tag>}
                      {rate.no_amend && <Tag color="orange" bordered={false}>{intl.formatMessage({ id: 'rate.non_amendable', defaultMessage: 'Non-amendable' })}</Tag>}
                      {rate.no_cancel && <Tag color="red" bordered={false}>{intl.formatMessage({ id: 'rate.non_cancellable', defaultMessage: 'Non-cancellable' })}</Tag>}
                      {rate.no_refund && <Tag color="magenta" bordered={false}>{intl.formatMessage({ id: 'rate.non_refundable', defaultMessage: 'Non-refundable' })}</Tag>}
                    </Space>
                  </div>

                </List.Item>
              );
            }}
          />
        </Col>
      </Row>
    </Card>
  );
};

// Re-render เฉพาะเมื่อ props ของ "ห้องพักนี้" เปลี่ยนจริง ๆ — ไม่สนใจ qty ของห้องพักอื่นใน qtyMap
const RoomCard = React.memo(RoomCardComponent, (prev, next) => {
  if (
    prev.roomData !== next.roomData ||
    prev.isMobile !== next.isMobile ||
    prev.getCartKey !== next.getCartKey ||
    prev.onQtyChange !== next.onQtyChange
  ) {
    return false;
  }
  return prev.roomData.rates.every((rate) => {
    const key = prev.getCartKey(prev.roomData, rate);
    return prev.qtyMap[key] === next.qtyMap[key];
  });
});

export default RoomCard;